import { useMemo, useState } from "react";
import { api } from "../../api";
import { useJdFields, useJdStatus, type JdFieldRow } from "../../hooks/queries";
import { BulkSessionView } from "../../components/BulkSessionView";
import { JdForm } from "./BulkJdFlowView";

interface JdStart {
  sessionId: string;
  queued: number;
  failedNames: string[];
  extraSessions: number;
}

function selectionOf(
  fields: JdFieldRow[] | undefined,
  query: string,
  picked: Set<string>,
) {
  const list = fields ?? [];
  const q = query.trim().toLowerCase();
  const shown = !q
    ? list
    : list.filter(
        (f) =>
          (f.name ?? "").toLowerCase().includes(q) ||
          (f.orgName ?? "").toLowerCase().includes(q),
      );
  const selectableAll = list.filter((f) => !f.linkedFieldId);
  const selectableShown = shown.filter((f) => !f.linkedFieldId);
  return {
    shown,
    selectableAll,
    selectableShown,
    allPicked:
      selectableAll.length > 0 && selectableAll.every((f) => picked.has(f.id)),
    allShownPicked:
      selectableShown.length > 0 &&
      selectableShown.every((f) => picked.has(f.id)),
    pickedAlreadyIn: list.filter((f) => picked.has(f.id) && f.linkedFieldId),
  };
}

// One session per JD org. A selection spanning orgs dispatches sequentially.
async function runImport(
  fields: JdFieldRow[] | undefined,
  picked: Set<string>,
): Promise<
  { ok: true; started: JdStart } | { ok: false; err: string; sent: string[] }
> {
  const rows = (fields ?? []).filter((f) => picked.has(f.id));
  const sent = rows.map(
    (r) =>
      `org ${r.jdOrgId || "—"} / field ${r.id}${r.name ? ` (${r.name})` : ""}`,
  );
  try {
    const byOrg = new Map<string, JdFieldRow[]>();
    for (const r of rows) {
      const l = byOrg.get(r.jdOrgId);
      if (l) l.push(r);
      else byOrg.set(r.jdOrgId, [r]);
    }
    const nameOf = new Map(rows.map((r) => [r.id, r.name ?? r.id]));
    const results = [];
    for (const [orgId, group] of byOrg) {
      results.push(
        await api.beginBulkJd(
          orgId,
          group.map((g) => ({
            jd_field_id: g.id,
            name: g.name ?? undefined,
          })),
        ),
      );
    }
    const failedIds = results.flatMap((r) => r.jd_failed ?? []);
    return {
      ok: true,
      started: {
        sessionId: results[0].session_id,
        queued: results.reduce((n, r) => n + (r.jd_queued ?? 0), 0),
        failedNames: failedIds.map((id) => nameOf.get(id) ?? id),
        extraSessions: results.length - 1,
      },
    };
  } catch (e) {
    return {
      ok: false,
      err:
        e instanceof Error
          ? e.message
          : "Something went wrong bringing those fields in.",
      sent,
    };
  }
}

function JdQueued({
  started,
  pickedCount,
}: {
  started: JdStart;
  pickedCount: number;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <p className="text-[11px] text-muted">
        {started.queued} of {pickedCount} field{pickedCount === 1 ? "" : "s"}{" "}
        queued.
        {started.extraSessions > 0 &&
          ` (${started.extraSessions} more drop${started.extraSessions === 1 ? "" : "s"} started for your other John Deere organization${started.extraSessions === 1 ? "" : "s"}.)`}
      </p>
      {started.failedNames.length > 0 && (
        <div className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px]">
          John Deere returned no boundary for: {started.failedNames.join(", ")}{" "}
          — each is recorded in your Errors menu.
        </div>
      )}
      <BulkSessionView
        sessionId={started.sessionId}
        startedNow
        fieldCount={started.queued}
      />
    </div>
  );
}

export function JdFlow() {
  const { data: jd, isLoading, isError, error } = useJdFields();
  // JD connection state is the authoritative check: a user who isn't connected must be told
  // that, not that their account has no fields.
  const jdStatus = useJdStatus();
  const fields = jd?.rows;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<string[] | null>(null);
  const [started, setStarted] = useState<JdStart | null>(null);
  const sel = useMemo(
    () => selectionOf(fields, query, picked),
    [fields, query, picked],
  );

  async function bringIn() {
    setErr(null);
    setSent(null);
    setBusy(true);
    const result = await runImport(fields, picked);
    if (result.ok) setStarted(result.started);
    else {
      setErr(result.err);
      setSent(result.sent);
    }
    setBusy(false);
  }

  if (started) return <JdQueued started={started} pickedCount={picked.size} />;

  return (
    <JdForm
      query={query}
      setQuery={setQuery}
      sel={sel}
      setPicked={setPicked}
      isLoading={isLoading}
      isError={isError}
      error={error}
      fields={fields}
      jd={jd}
      status={jdStatus.data}
      picked={picked}
      err={err}
      sent={sent}
      busy={busy}
      onBringIn={() => void bringIn()}
    />
  );
}
